import React, { useMemo } from 'react';
import { useTypedSelector } from '../../shared/hooks/useTypedSelector';
import {
    Grid,
    makeStyles,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@material-ui/core';
import { ITheme } from '../../themes/custom/ITheme';
import clsx from 'clsx';
import usePrices from '../../shared/hooks/usePrices';
import { IIndexable } from '../GraphicsLibrary/GraphicDetail/GraphicDetail';
import { SelectedSize } from '../../schemas/onlineDesigner';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';

const useStyles = makeStyles((theme: ITheme) => {
    return {
        container: {
            marginBottom: '20px',
        },
        color: {
            color: (props: Props): string =>
                props.isMobile
                    ? theme.custom.palette.black
                    : theme.custom.palette.white,
        },
        smallTitle: {
            width: (props: Props): string => (props.isMobile ? '50%' : '200px'),
            fontSize: '15px',
            fontWeight: 600,
            lineHeight: '18px',
            paddingTop: '0px',
            textAlign: 'right',
        },
        textLeft: {
            textAlign: 'left',
        },
        bigTitle: {
            width: (props: Props): string => (props.isMobile ? '50%' : '200px'),
            fontSize: (props: Props): string =>
                props.isMobile ? '15px' : '24px',
            lineHeight: '30px',
            fontWeight: 600,
            textTransform: 'uppercase',
            textAlign: 'right',
            paddingBottom: '0px',
            paddingTop: '0px',
            whiteSpace: 'nowrap',
        },
        smallPrice: {
            fontSize: '15px',
            lineHeight: '18px',
            fontWeight: 300,
            textAlign: 'right',
            paddingTop: '0px',
        },
        bigPrice: {
            fontSize: (props: Props): string =>
                props.isMobile ? '15px' : '24px',
            lineHeight: '15px',
            fontWeight: 300,
            textAlign: 'right',
            paddingBottom: '0px',
            paddingTop: '0px',
            whiteSpace: 'nowrap',
        },
        bigPriceNote: {
            fontSize: '12px',
            lineHeight: '15px',
            textAlign: 'right',
            paddingTop: '0px',
        },
        noBorderCell: {
            borderBottom: 'none',
            textAlign: 'right',
            padding: '0px 16px',
        },
        mobileCell: {
            textAlign: 'left',
            padding: (props: Props): string =>
                props.isMobile ? '5px' : '0px 16px 12px 0 ',
            margin: (props: Props): string => (props.isMobile ? '0' : '12px '),
        },
        mobileCellRight: {
            textAlign: 'right',
        },
        borderBottom: {
            display: 'block',
            borderBottom: '1px solid white',
            padding: (props: Props): string =>
                props.isMobile ? '5px' : '0px 16px 12px 0 ',
            margin: (props: Props): string =>
                props.isMobile ? '12px 0px 0' : '12px 0px',
        },
        addOns: {
            padding: '0px',
        },
    };
});

interface Props {
    isMobile: boolean;
}

const CartSummary: React.FC<Props> = (props) => {
    const classes = useStyles(props);
    const { t } = useTranslation();
    const { isMobile } = props;
    const isTeam = (window as IIndexable)[window.GET_IS_TEAM_FCE_NAME]();

    const { data: selectedData } = useTypedSelector(
        (state) => state.od.selected
    );
    const { data: productData, loading: productLoading } = useTypedSelector(
        (state) => state.od.loadProduct
    );

    const designExtraPrice = (window as IIndexable)[
        window.GET_DESIGN_EXTRA_PRICE_FCE_NAME
    ]();
    const unitAddOnPrice = (window as IIndexable)[
        window.GET_ADD_ONS_PRICE_FCE_NAME
    ]();

    const { displayPrice } = usePrices(productData?.currency);

    const { data: teamData } = useTypedSelector((state) => state.od.team);

    const selectedSizes = useMemo<SelectedSize[]>((): SelectedSize[] => {
        if (
            teamData !== null &&
            teamData.filter(
                (teamMember) =>
                    teamMember.size !== null ||
                    teamMember.name.length !== 0 ||
                    teamMember.number.length !== 0
            ).length > 0
        ) {
            const sizes: SelectedSize[] = [];
            productData?.sizes.forEach((size) => {
                const sumQuantity = teamData.filter(
                    (teamMember) =>
                        teamMember.size &&
                        teamMember.size.idAttribute === size.idAttribute
                ).length;
                sizes.push({
                    size: size,
                    quantity: sumQuantity,
                });
            });
            return sizes;
        } else {
            return selectedData && selectedData.sizes ? selectedData.sizes : [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedData, teamData, isTeam]);

    const addOnsPrice = useMemo<number>((): number => {
        const sumQuantity = selectedSizes
            .map((size) => size.quantity)
            .reduce((a, b) => a + b, 0);
        return (
            sumQuantity *
            unitAddOnPrice *
            (1 + (productData ? productData.tax : 0))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSizes, unitAddOnPrice]);

    const totalReduction = useMemo<number>((): number => {
        let reduction = 0;
        if (selectedSizes && productData?.discounts) {
            const sumQuantity = selectedSizes
                .map((size) => size.quantity)
                .reduce((a, b) => a + b, 0);

            productData?.discounts.forEach((discount) => {
                if (
                    discount.reductionType === 'percentage' &&
                    discount.fromQuantity <= sumQuantity
                ) {
                    reduction = discount.reduction;
                }
            });
        }
        return reduction;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSizes, productData]);

    const totalPrice = useMemo<number>((): number => {
        let totalPrice = 0;
        if (
            productData &&
            selectedData &&
            selectedData.fabric &&
            selectedSizes &&
            selectedSizes.length > 0
        ) {
            selectedSizes.forEach((selectedSize) => {
                if (
                    selectedSize.quantity > 0 &&
                    selectedData &&
                    selectedData.fabric
                ) {
                    totalPrice +=
                        (selectedSize.quantity *
                            Math.round(
                                (productData.price +
                                    selectedSize.size.price +
                                    selectedData.fabric.price +
                                    designExtraPrice +
                                    unitAddOnPrice) *
                                    (1 - totalReduction) *
                                    (1 + productData.tax) *
                                    100
                            )) /
                        100;
                }
            });
        }

        return totalPrice;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productData, selectedData, selectedSizes, unitAddOnPrice]);

    const totalTax = useMemo<number>((): number => {
        let totalTax = 0;
        if (
            productData &&
            selectedData &&
            selectedData.fabric &&
            selectedSizes &&
            selectedSizes.length > 0
        ) {
            selectedSizes.forEach((selectedSize) => {
                if (
                    selectedSize.quantity > 0 &&
                    selectedData &&
                    selectedData.fabric
                ) {
                    totalTax +=
                        (selectedSize.quantity *
                            Math.round(
                                (productData.price +
                                    selectedSize.size.price +
                                    selectedData.fabric.price +
                                    designExtraPrice +
                                    +unitAddOnPrice) *
                                    (1 - totalReduction) *
                                    productData.tax *
                                    100
                            )) /
                        100;
                }
            });
        }
        return totalTax;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productData, selectedData, selectedSizes, unitAddOnPrice]);

    return (
        <React.Fragment>
            {productLoading && (
                <React.Fragment>
                    <Skeleton
                        variant="rectangular"
                        width={'100%'}
                        height={40}
                        animation="wave"
                    />
                </React.Fragment>
            )}
            {!productLoading && productData && (
                <Grid justifyContent="space-between" container wrap="nowrap">
                    {!isMobile && <Grid item xs={2} />}
                    <Grid item {...{ xs: isMobile ? 12 : 10 }}>
                        <TableContainer className={classes.container}>
                            <Table>
                                <TableHead>
                                    {addOnsPrice !== 0 && (
                                        <TableRow>
                                            <TableCell
                                                className={clsx(
                                                    classes.noBorderCell,
                                                    classes.smallTitle,
                                                    classes.addOns,
                                                    classes.color
                                                )}
                                            >
                                                <span
                                                    className={clsx(
                                                        classes.borderBottom,
                                                        isMobile &&
                                                            classes.textLeft
                                                    )}
                                                >
                                                    {t('Total Add-Ons')}
                                                </span>
                                            </TableCell>
                                            <TableCell
                                                className={clsx(
                                                    classes.smallPrice,
                                                    classes.noBorderCell,
                                                    classes.addOns,
                                                    classes.color,
                                                    isMobile &&
                                                        classes.mobileCellRight
                                                )}
                                            >
                                                <span
                                                    className={
                                                        classes.borderBottom
                                                    }
                                                >
                                                    {displayPrice(addOnsPrice)}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow>
                                        <TableCell
                                            className={clsx(
                                                classes.bigTitle,
                                                classes.noBorderCell,
                                                classes.color,
                                                isMobile && classes.mobileCell
                                            )}
                                        >
                                            {t('Design total')}
                                            <br />
                                        </TableCell>
                                        <TableCell
                                            className={clsx(
                                                classes.bigPrice,
                                                classes.noBorderCell,
                                                classes.color,
                                                isMobile && classes.mobileCell,
                                                isMobile &&
                                                    classes.mobileCellRight
                                            )}
                                        >
                                            {displayPrice(totalPrice)}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell
                                            className={clsx(
                                                classes.noBorderCell,
                                                isMobile && classes.mobileCell
                                            )}
                                        />
                                        <TableCell
                                            className={clsx(
                                                classes.noBorderCell,
                                                classes.color,
                                                isMobile && classes.mobileCell,
                                                isMobile &&
                                                    classes.mobileCellRight
                                            )}
                                        >
                                            {productData.tax > 0 && (
                                                <React.Fragment>
                                                    {t('Incl GST.')} (
                                                    {displayPrice(totalTax)})
                                                </React.Fragment>
                                            )}
                                            {productData.tax === 0 && (
                                                <React.Fragment>
                                                    {t('Excl GST.')}
                                                </React.Fragment>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {totalReduction > 0 && (
                                        <TableRow>
                                            <TableCell
                                                className={clsx(
                                                    classes.smallTitle,
                                                    classes.noBorderCell,
                                                    classes.color,
                                                    isMobile &&
                                                        classes.mobileCell
                                                )}
                                            >
                                                {t('Total savings')}
                                            </TableCell>
                                            <TableCell
                                                className={clsx(
                                                    classes.noBorderCell,
                                                    classes.color,
                                                    isMobile &&
                                                        classes.mobileCell,
                                                    isMobile &&
                                                        classes.mobileCellRight
                                                )}
                                            >
                                                {displayPrice(
                                                    totalPrice * totalReduction
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableHead>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            )}
        </React.Fragment>
    );
};

export default CartSummary;
